import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Star, Clock, Eye } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Category icons mapping
const CATEGORY_ICONS = {
  vaccination: '💉',
  prevention: '🛡️',
  nutrition: '🥗',
  comportement: '🧠',
  urgences: '🚨',
  soins: '🩺',
  adoption: '🏠',
  autre: '📝'
};

const ConseilsSection = () => {
  const [featuredArticles, setFeaturedArticles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchFeaturedArticles();
  }, []);

  const fetchFeaturedArticles = async () => {
    try {
      // Fetch featured articles from API
      const response = await fetch(`${API_URL}/api/conseils/?featured=true&limit=3`);
      if (response.ok) {
        const data = await response.json();
        setFeaturedArticles(data);
      }
    } catch (error) {
      console.error('Error fetching featured articles:', error);
    }
    setIsLoading(false);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  if (isLoading) {
    return (
      <section className="py-16 bg-gray-50 dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-4 border-green-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        </div>
      </section>
    );
  }

  if (featuredArticles.length === 0) {
    return null; // Don't show section if no featured articles
  }

  return (
    <section className="py-16 bg-gray-50 dark:bg-gray-900" data-testid="conseils-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-10">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Star className="w-6 h-6 text-yellow-500 fill-yellow-500" />
              <span className="text-sm font-medium text-yellow-600 dark:text-yellow-400 uppercase tracking-wide">
                À la une
              </span>
            </div>
            <h2 className="text-3xl font-bold text-gray-800 dark:text-white">
              Nos conseils vedettes
            </h2>
            <p className="text-gray-500 dark:text-gray-400 mt-2">
              Les articles les plus utiles pour prendre soin de vos animaux
            </p>
          </div>
          <Link
            to="/conseils"
            className="hidden md:inline-flex items-center gap-2 px-4 py-2 bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 rounded-full font-medium hover:bg-green-200 dark:hover:bg-green-800 transition-colors"
          >
            Tous les articles
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        {/* Featured Articles Grid */}
        <div className="grid md:grid-cols-3 gap-6">
          {featuredArticles.map((article, index) => (
            <Link
              key={article.article_id}
              to={`/conseils/${article.slug}`}
              className={`group bg-white dark:bg-gray-800 rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100 dark:border-gray-700 ${
                index === 0 ? 'md:row-span-2 md:col-span-2' : ''
              }`}
              data-testid={`featured-article-${article.article_id}`}
            >
              {/* Image */}
              <div className={`relative overflow-hidden ${index === 0 ? 'h-64 md:h-80' : 'h-48'}`}>
                {article.cover_image ? (
                  <img
                    src={article.cover_image}
                    alt={article.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-green-100 to-emerald-100 dark:from-green-900 dark:to-emerald-900 flex items-center justify-center">
                    <span className="text-6xl">{CATEGORY_ICONS[article.category] || '📝'}</span>
                  </div>
                )}
                
                {/* Featured Badge */}
                <div className="absolute top-4 left-4">
                  <span className="inline-flex items-center gap-1 px-3 py-1 bg-yellow-500 text-white text-xs font-bold rounded-full shadow-lg">
                    <Star className="w-3 h-3 fill-white" />
                    Vedette
                  </span>
                </div>
                
                {/* Category Badge */}
                <div className="absolute top-4 right-4">
                  <span className="inline-flex items-center gap-1 px-3 py-1 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm text-gray-700 dark:text-gray-200 text-xs font-medium rounded-full">
                    {CATEGORY_ICONS[article.category]}
                    {article.category}
                  </span>
                </div>
                
                {/* Gradient overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </div>

              {/* Content */}
              <div className={`p-5 ${index === 0 ? 'md:p-6' : ''}`}>
                {/* Meta */}
                <div className="flex items-center gap-3 mb-3 text-xs text-gray-500 dark:text-gray-400">
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {formatDate(article.published_at)}
                  </span>
                  <span className="flex items-center gap-1">
                    <Eye className="w-3 h-3" />
                    {article.view_count || 0} lectures
                  </span>
                </div>

                {/* Title */}
                <h3 className={`font-bold text-gray-800 dark:text-white group-hover:text-green-600 dark:group-hover:text-green-400 transition-colors mb-2 ${
                  index === 0 ? 'text-xl md:text-2xl' : 'text-lg'
                }`}>
                  {article.title}
                </h3>

                {/* Excerpt */}
                <p className={`text-gray-500 dark:text-gray-400 ${index === 0 ? 'line-clamp-3' : 'line-clamp-2'} text-sm`}>
                  {article.excerpt}
                </p>

                {/* Tags */}
                {article.tags && article.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-4">
                    {article.tags.slice(0, 3).map((tag, idx) => (
                      <span
                        key={idx}
                        className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-xs rounded-full"
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>
                )}

                {/* Read More */}
                <div className="mt-4 flex items-center gap-2 text-green-600 dark:text-green-400 font-medium text-sm group-hover:gap-3 transition-all">
                  Lire l'article
                  <ArrowRight className="w-4 h-4" />
                </div>
              </div>
            </Link>
          ))}
        </div>

        {/* Mobile CTA */}
        <div className="mt-10 text-center md:hidden">
          <Link
            to="/conseils"
            className="inline-flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-full font-medium hover:bg-green-700 transition-colors"
          >
            Découvrir tous nos conseils
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default ConseilsSection;
